import React, { useEffect, useRef, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult
} from 'react-beautiful-dnd';
import styled from 'styled-components';

import {
  Box,
  Button,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  ListItemProps,
  ListItemText,
  Paper
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import ClearIcon from '@material-ui/icons/Clear';
import DragIndicatorIcon from '@material-ui/icons/DragIndicator';
import { Editor } from '@tinymce/tinymce-react';

import CardTitle from '../../../common/card/CardTitle';
import { useUpdateAgreement } from '../Agreement.hooks';
import { Agreement, AgreementSection } from '../Agreement.interface';
import environment from '../../../environments';
import { PublishStatus } from '../../../common/PublishStatusLabels';

const StyledGrid = styled(Grid)`
  flex: 1;
  .grid-flex,
  .card-flex,
  .card-content-flex {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .tox-tinymc {
    flex: 1;
  }
`;

const StyledListItem = styled(ListItem)`
  background: #fff;
  border-bottom: solid 1px #ccc;
` as React.ComponentType<ListItemProps>;

const reorder = (
  sections: AgreementSection[],
  startIndex: number,
  endIndex: number
) => {
  const result = Array.from(sections);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

export interface AgreementTabEditProps {
  agreement: Agreement;
  onChange?: (agreement: Agreement) => void;
}

const AgreementTabEdit: React.FC<AgreementTabEditProps> = ({
  agreement,
  onChange
}) => {
  const onChangeRef = useRef(onChange);
  const [sections, setSections] = useState<AgreementSection[]>(
    agreement && agreement.sections && agreement.sections.length > 0
      ? agreement.sections
      : [
          {
            name: 'Untitled',
            body: ''
          }
        ]
  );
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
  const section = sections[selectedSectionIndex];
  const {
    agreement: updatedAgreement,
    patch,
    isLoading: isUpdating
  } = useUpdateAgreement(agreement.id);

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;
    // dropped outside the list
    if (!destination) {
      return;
    }
    const reorderedSections = reorder(
      sections,
      source.index,
      destination.index
    );
    // console.log('reorderedSections', reorderedSections);
    setSections(reorderedSections);
  };

  useEffect(() => {
    if (updatedAgreement && onChangeRef && onChangeRef.current) {
      onChangeRef.current(updatedAgreement);
    }
  }, [updatedAgreement]);

  const handleSave = () => {
    patch({ sections });
  };

  const isEditable = agreement.status !== PublishStatus.Published;

  return (
    <StyledGrid container spacing={2}>
      <Grid item xs={12} md={4} className='grid-flex'>
        <Paper className='card-flex'>
          <CardTitle>Agreement Sections</CardTitle>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId='terms'>
              {(provided, snapshot) => (
                <List ref={provided.innerRef}>
                  <Divider component='li' />

                  {sections.map((section, index) => (
                    <Draggable
                      key={index}
                      draggableId={section.name}
                      index={index}
                      isDragDisabled={!isEditable}
                    >
                      {(provided, snapshot) => (
                        <StyledListItem
                          onClick={() => setSelectedSectionIndex(index)}
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          <ListItemIcon>
                            <DragIndicatorIcon />
                          </ListItemIcon>
                          <InputBase
                            style={{ width: '100%' }}
                            value={section.name}
                            disabled={!isEditable}
                            onChange={event => {
                              const copy = [...sections];
                              copy[index].name = event.target.value;
                              setSections(copy);
                            }}
                          />
                          {sections.length > 1 && (
                            <IconButton
                              size='small'
                              disabled={!isEditable}
                              onClick={event => {
                                event.stopPropagation();
                                if (
                                  selectedSectionIndex ===
                                  sections.length - 1
                                ) {
                                  setSelectedSectionIndex(
                                    selectedSectionIndex - 1
                                  );
                                }
                                sections.splice(index, 1);
                                setSections([...sections]);
                              }}
                            >
                              <ClearIcon />
                            </IconButton>
                          )}
                        </StyledListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                  {isEditable && (
                    <ListItem
                      button
                      onClick={() => {
                        const copy = [...sections];
                        copy.push({
                          name: 'Untitled',
                          body: ''
                        });
                        setSections(copy);
                      }}
                    >
                      <ListItemIcon>
                        <AddIcon />
                      </ListItemIcon>
                      <ListItemText primary='Add Section' />
                    </ListItem>
                  )}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </Paper>
      </Grid>
      <Grid item xs={12} md={8} className='grid-flex'>
        <Paper className='card-flex'>
          <CardTitle>Agreement Section Content</CardTitle>
          <CardContent className='card-content-flex'>
            <Editor
              apiKey={environment.tinyApiKey}
              value={section.body}
              init={{
                height: '100%',
                menubar: false,
                contextmenu: false,
                browser_spellcheck: true,
                plugins: [
                  'advlist autolink lists link image charmap print preview anchor',
                  'searchreplace visualblocks code fullscreen',
                  'insertdatetime media table paste code help wordcount'
                ],
                toolbar:
                  'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | link bullist numlist outdent indent | removeformat | help | code'
              }}
              disabled={!isEditable}
              onEditorChange={(content, editor) => {
                // console.log('onEditorChange', content);
                const copy = [...sections];
                copy[selectedSectionIndex].body = content;
                setSections(copy);
              }}
            />
          </CardContent>
          <CardActions>
            <Box
              flex={1}
              display='flex'
              alignItems='center'
              justifyContent='flex-end'
              mr={1}
            >
              {!isEditable && <Box mr={1}>Start a new version to edit</Box>}
              {isUpdating && (
                <Box mr={1}>
                  <CircularProgress size={20} />
                </Box>
              )}
              <Button
                variant='contained'
                color='primary'
                onClick={handleSave}
                disabled={isUpdating || !isEditable}
              >
                Save
              </Button>
            </Box>
          </CardActions>
        </Paper>
      </Grid>
    </StyledGrid>
  );
};

export default AgreementTabEdit;
